import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Card, Modal, PillButton } from '../ui';
import { apiErrorMessage } from '../utils/apiError';
import { cliCommand, dotenvText, isRefText, templateText } from '../utils/environment';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as LinkIcon } from '../assets/icons/link.svg';
import { ReactComponent as TerminalIcon } from '../assets/icons/terminal.svg';
import './EnvironmentView.css';

/**
 * Las variables de un entorno en el detalle del item: valores ocultos (ojo por fila), copiar el nombre o el valor, y exportar
 * SOLO al portapapeles: la plantilla (referencias, sin valores) o el .env con los valores resueltos por el servidor (con aviso).
 */
function EnvironmentView({ item }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'error', text }

  const variables = Array.isArray(item.data.variables) ? item.data.variables.filter((v) => v && v.key) : [];
  const base = typeof item.data.extends === 'string' ? item.data.extends.trim() : '';

  const copy = (text) => navigator.clipboard.writeText(text);

  const copyTemplate = async () => {
    await copy(templateText({ id: item.id, vault: item.data.vault, variables }));
    setNotice({ kind: 'ok', text: t('environment.exportTemplateDone') });
  };

  const copyValues = async () => {
    setBusy(true);
    try {
      // El servidor resuelve las referencias y la herencia; el panel solo recibe el resultado.
      const res = await api.get(`/env/${encodeURIComponent(item.name)}`);
      await copy(dotenvText(res.data.variables));
      setNotice({ kind: 'ok', text: t('environment.exportDone') });
    } catch (err) {
      setNotice({ kind: 'error', text: apiErrorMessage(err, t, 'environment.exportError') });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <Card className="sgc-env-view">
      <div className="sgc-env-view__toolbar">
        <h2 className="sgc-env-view__title">{t('environment.variables')} <span className="sgc-env-view__count">{variables.length}</span></h2>
        <div className="sgc-env-view__actions">
          <PillButton variant="secondary" size="sm" icon={CopyIcon} onClick={copyTemplate}>{t('environment.exportTemplate')}</PillButton>
          <PillButton variant="secondary" size="sm" icon={CopyIcon} onClick={() => setConfirm(true)}>{t('environment.exportValues')}</PillButton>
        </div>
      </div>
      {notice && <p className={`sgc-env-view__notice sgc-env-view__notice--${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</p>}
      {base && <p className="sgc-env-view__base">{t('environment.extendsLabel')}: <code>{base}</code></p>}

      {variables.length === 0 ? (
        <p className="sgc-env-view__empty">{t('environment.empty')}</p>
      ) : (
        <ul className="sgc-env-view__list">
          {variables.map((v) => {
            const ref = v.ref || (isRefText(v.value) ? v.value : '');
            const shown = revealed[v.key];
            return (
              <li key={v.key} className="sgc-env-view__row">
                <code className="sgc-env-view__key">{v.key}</code>
                <span className="sgc-env-view__value">
                  {ref ? (
                    <><LinkIcon width={14} height={14} aria-hidden="true" /><code>{ref}</code></>
                  ) : (
                    <code>{shown ? String(v.value ?? '') : '••••••••'}</code>
                  )}
                </span>
                <span className="sgc-env-view__row-actions">
                  {!ref && (
                    <button type="button" onClick={() => setRevealed((p) => ({ ...p, [v.key]: !p[v.key] }))} title={t('dashboard.reveal')} aria-label={t('dashboard.reveal')} aria-pressed={Boolean(shown)}>
                      {shown ? <EyeOffIcon width={16} height={16} aria-hidden="true" /> : <EyeIcon width={16} height={16} aria-hidden="true" />}
                    </button>
                  )}
                  <button type="button" onClick={() => copy(v.key)} title={t('environment.copyKey')} aria-label={t('environment.copyKey')}>
                    <CopyIcon width={16} height={16} aria-hidden="true" />
                  </button>
                  {!ref && (
                    <button type="button" onClick={() => copy(String(v.value ?? ''))} title={t('dashboard.copy')} aria-label={t('environment.copyValue')}>
                      <CopyIcon width={16} height={16} aria-hidden="true" />
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="sgc-env-view__cli">
        <TerminalIcon width={16} height={16} aria-hidden="true" />
        <div>
          <strong>{t('environment.cliTitle')}</strong>
          <code>{cliCommand(item.name)}</code>
        </div>
        <button type="button" onClick={() => copy(cliCommand(item.name))}>{t('environment.cliCopy')}</button>
      </div>

      <Modal open={confirm} onClose={() => setConfirm(false)} title={t('environment.exportConfirmTitle')}>
        <p>{t('environment.exportConfirmBody')}</p>
        <div className="sgc-env-view__confirm">
          <PillButton variant="secondary" onClick={() => setConfirm(false)}>{t('common.cancel')}</PillButton>
          <PillButton variant="danger" loading={busy} onClick={copyValues}>{t('environment.exportConfirmAction')}</PillButton>
        </div>
      </Modal>
    </Card>
  );
}

export default EnvironmentView;
